import { useCallback, useEffect, useRef } from "react";

import type { GeneratedSoundItem } from "@ui/components/audio/AudioGenerationWidget";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { env } from "@/env";
import { logger } from "@utilities/logger";

import cover1 from "../images/audio-cover-1.png";
import cover2 from "../images/audio-cover-2.png";
import cover3 from "../images/audio-cover-3.png";
import cover4 from "../images/audio-cover-4.png";

const GENERATED_COVERS = [cover1, cover2, cover3, cover4];

interface MusicGenerationResponse {
  download_url: string;
  id: string;
  metadata: {
    title: string;
    description: string;
  };
  generation_settings: {
    lyrics: { positive_global_styles: string[] };
    prompt: string;
  };
}

interface UseMusicGenerationParams {
  showSignUpPopover: (trigger: string) => void;
}

export function useMusicGeneration({ showSignUpPopover }: UseMusicGenerationParams) {
  const blobUrlsRef = useRef<string[]>([]);
  const generationRoundRef = useRef(0);

  useEffect(() => {
    const urls = blobUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  const generateItems = useCallback(
    async (prompt: string, _loop: boolean): Promise<GeneratedSoundItem[]> => {
      captureEvent(EventName.marketing_website_music_demo_generate, { source: "programmatic" });

      try {
        const res = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/music/detailed/anonymous`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ prompt }),
        });

        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          const errorStatus = body?.detail?.status;
          if (errorStatus === "quota_exceeded") {
            captureEvent(EventName.marketing_website_music_demo_quota_exceeded, { source: "programmatic" });
            showSignUpPopover("generation_quota_exceeded");
          } else {
            captureEvent(EventName.marketing_website_music_demo_error, {
              error: errorStatus ?? `HTTP ${res.status}`,
              source: "programmatic",
            });
          }
          throw new Error(errorStatus ?? `HTTP ${res.status}`);
        }

        const data: MusicGenerationResponse = await res.json();

        captureEvent(EventName.mw_music_category_generation_successful, {
          generationCount: 1,
          source: "programmatic",
        });

        const round = ++generationRoundRef.current;
        const id = data.id ?? `generated-${round}-0`;

        // Fetch the audio as a blob for local playback
        let audioUrl: string | undefined;
        try {
          const audioRes = await fetch(data.download_url);
          if (audioRes.ok) {
            const blob = await audioRes.blob();
            audioUrl = URL.createObjectURL(blob);
            blobUrlsRef.current.push(audioUrl);
          }
        } catch (err) {
          logger.warn("[useMusicGeneration] Failed to fetch audio blob", { err });
          // Fall back to direct URL
          audioUrl = data.download_url;
        }

        return [
          {
            id,
            title: data.metadata?.title ?? `Generation 1`,
            audioUrl,
            coverImage: GENERATED_COVERS[0].src,
          },
        ];
      } catch (err) {
        if (!(err instanceof Error && err.message.includes("quota_exceeded"))) {
          // quota errors already captured above — avoid double-capture
          const isQuota =
            typeof err === "object" &&
            err !== null &&
            "message" in err &&
            String((err as { message: string }).message).includes("quota_exceeded");
          if (!isQuota) {
            captureEvent(EventName.marketing_website_music_demo_error, {
              error: err instanceof Error ? err.message : "Unknown error",
              source: "programmatic",
            });
          }
        }
        throw err;
      }
    },
    [showSignUpPopover],
  );

  const handleGenerateNew = useCallback(() => {
    blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    blobUrlsRef.current = [];
  }, []);

  return { generateItems, handleGenerateNew };
}
